import store from '@/store';
import Axios, {AxiosInstance, AxiosResponse, isAxiosError} from 'axios';
import router from '@/router';
import CrudAccess from '@/classes/clientOnly/permissionTreeResources/interfaces/CrudAccess';
import {LoginResponseDto} from '@/classes/dto/authenticator/response/LoginResponseDto';
import {CheckLoginResponseDto} from '@/classes/dto/authenticator/response/CheckLogin.response.dto';
import {Types} from 'mongoose';
import {NotifierSocketApi} from '@/classes/api/notifier.socket.api';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {ExceptionsCollector} from '@/exceptions/ExceptionsCollector';
import {VanillaException} from '@/exceptions/VanillaException';
import {LoginTemporarilyRequestDto} from '@/classes/dto/authenticator/request/LoginTemporarily.request.dto';
import {forgetLoginAsFrom, markAccountSwitch, reloadAfterSwitch, rememberLoginAsFrom, TEMP_LOGIN_FROM_KEY} from '@/helpers/accountSwitch.helper';
import {
    isViewAsRole,
    readViewAsRoleFlag,
    VIEW_AS_ROLE_ENDED_TEXT,
    VIEW_AS_ROLE_EXPIRED_TEXT,
    ViewAsRole,
    writeViewAsRoleFlag,
} from '@/helpers/viewAsRole.helper';

export type ViewAsRoleAccess = { canUse: boolean; active: ViewAsRole | null; minutes: number };

/** How long the loaded crud and gui access are used before they are refreshed in the background */
const CRUD_MAX_AGE_MS = 60 * 1000;

/**
 * This class is used for communicating with Institutioner microservice
 * @class InstitutionerApi
 * @author mklaczinski
 */
export class LoginApi {
    private static service = 'authenticator';
    private static accessToken = '';
    /** Time the crud and gui access were loaded last, 0 if they have to be (re)loaded */
    private static crudLoadedAt = 0;
    private static crudRequest: Promise<void> | null = null;

    static async login(mail: string, password: string): Promise<LoginResponseDto> {
        try {
            const credentials = (await Axios.post(`${process.env.VUE_APP_API_PATH}/${this.service}/login`, {
                username: mail,
                password,
            }, {
                withCredentials: true,
            })).data;
            this.handleCredentials(credentials);
            await NotifierSocketApi.connect();
            return credentials;
        } catch (e) {
            throw this.transformException(e);
        }
    }

    static async loginAs(employeeId: Types.ObjectId) {
        try {
            const credentials = (await Axios.post(`${process.env.VUE_APP_API_PATH}/${this.service}/loginAs`, {
                employeeId,
            }, {
                withCredentials: true,
            })).data;
            const from = router.currentRoute.fullPath;
            this.handleCredentials(credentials);
            // Back via „Zurück zu meinem Account“ to this office page; the new login opens silently its area
            rememberLoginAsFrom(from);
            reloadAfterSwitch('loginAs', router.currentRoute.path);
        } catch (e) {
            throw this.transformException(e);
        }
    }

    static async loginTemporarily(data: LoginTemporarilyRequestDto) {
        const axiosInstance = store.state.axios;
        if (!axiosInstance) {
            throw new Error('Axios instance not defined');
        }
        try {
            const credentials = (await axiosInstance.post(`${process.env.VUE_APP_API_PATH}/${this.service}/loginTemporarily`, data, {
                withCredentials: true,
            })).data;
            this.handleCredentials(credentials);
            try {
                localStorage.setItem(TEMP_LOGIN_FROM_KEY, router.currentRoute.path);
            } catch (e) {
                // blocked storage: „Zurück zu meinem Account“ opens the start page
            }
            reloadAfterSwitch('temp', router.currentRoute.path);
        } catch (e) {
            throw this.transformException(e);
        }
    }

    /**
     * „Zurück zu meinem Account“: opens the office page before the switch (or „/“ instead of a page of the Lehrkraft-/
     * Bewerbungs-Bereich) without the hint „Diese Seite ist für dich nicht freigegeben.“; `notice` is shown after the reload.
     */
    static async loginAsOrigin(notice?: string) {
        try {
            const credentials = (await Axios.post(`${process.env.VUE_APP_API_PATH}/${this.service}/loginAsOrigin`, {}, {
                withCredentials: true,
            })).data;
            this.handleCredentials(credentials);
            reloadAfterSwitch('origin', router.currentRoute.path, notice ? {notice} : {});
        } catch (e) {
            if (isAxiosError(e) && e.response?.status === 401) {
                // no origin account (anymore): the way back is a fresh login, not a dead banner
                forgetLoginAsFrom();
                await this.logout({sessionExpired: true});
                this.reloadToLogin();
                return;
            }
            throw this.transformException(e);
        }
    }

    /** „Ansicht als Rolle“: darf ich sie öffnen? (Knopf in Einstellungen › Benutzer und Rollen) */
    static async viewAsRoleAccess(): Promise<ViewAsRoleAccess> {
        const axiosInstance = store.state.axios;
        if (!axiosInstance) {
            return {canUse: false, active: null, minutes: 60};
        }
        try {
            return (await axiosInstance.get(`${process.env.VUE_APP_API_PATH}/${this.service}/viewAsRole`)).data as ViewAsRoleAccess;
        } catch (e) {
            // older authenticator / no right: no button
            return {canUse: false, active: null, minutes: 60};
        }
    }

    /**
     * Startet die „Ansicht als Rolle“ (nur lesen): neues Token vom authenticator, danach Neuladen auf der Startseite
     * (Rechte, Menü und Bereich werden mit den Rechten der Rolle neu geladen; Rolle „Lehrkräfte“ → Lehrkraft-Bereich).
     */
    static async startViewAsRole(roleId: Types.ObjectId | string) {
        const axiosInstance = store.state.axios;
        if (!axiosInstance) {
            throw new Error('Axios instance not defined');
        }
        try {
            const credentials = (await axiosInstance.post(`${process.env.VUE_APP_API_PATH}/${this.service}/viewAsRole`, {roleId}, {
                withCredentials: true,
            })).data;
            this.handleCredentials(credentials);
            store.commit('setViewAsRole', isViewAsRole(credentials.viewAsRole) ? credentials.viewAsRole : null);
            try {
                // „Beenden“ leads back to this page (same key as the former test login)
                localStorage.setItem(TEMP_LOGIN_FROM_KEY, router.currentRoute.fullPath);
            } catch (e) {
                // blocked storage: „Beenden“ opens the start page
            }
            reloadAfterSwitch('temp', router.currentRoute.path);
        } catch (e) {
            throw this.transformException(e);
        }
    }

    /** Beendet die Ansicht (Banner-Knopf, Ablauf) und lädt den eigenen Account neu; wirft, wenn das nicht geht */
    static async endViewAsRole(notice: string = VIEW_AS_ROLE_ENDED_TEXT) {
        try {
            // cookie only: the authenticator checks the view token itself (also after it expired)
            const credentials = (await Axios.post(`${process.env.VUE_APP_API_PATH}/${this.service}/viewAsRole/end`, {}, {
                withCredentials: true,
            })).data;
            this.handleCredentials(credentials);
            store.commit('setViewAsRole', null);
            reloadAfterSwitch('origin', router.currentRoute.path, {notice});
        } catch (e) {
            writeViewAsRoleFlag(false);
            throw this.transformException(e);
        }
    }

    /**
     * Nach Ablauf der Ansicht lehnt jeder Dienst das Token ab (401). Ist eine Ansicht gemerkt, wird sie einmal still beendet
     * (zurück zum eigenen Account, solange die Sitzung läuft). true = neues Token liegt im Cookie.
     */
    private static async recoverExpiredViewAsRole(): Promise<boolean> {
        if (!readViewAsRoleFlag()) {
            return false;
        }
        writeViewAsRoleFlag(false);
        try {
            await Axios.post(`${process.env.VUE_APP_API_PATH}/${this.service}/viewAsRole/end`, {}, {withCredentials: true});
            markAccountSwitch({notice: VIEW_AS_ROLE_EXPIRED_TEXT});
            return true;
        } catch (e) {
            return false;
        }
    }

    static async checkLogin(retried = false): Promise<CheckLoginResponseDto> {
        try {
            let instance: AxiosInstance;
            if (this.accessToken) {
                instance = Axios.create({
                    headers: {
                        Authorization: `Bearer ${this.accessToken}`,
                    },
                    baseURL: process.env.VUE_APP_API_PATH,
                    withCredentials: true,
                });
            } else {
                instance = Axios.create({
                    baseURL: process.env.VUE_APP_API_PATH,
                    withCredentials: true,
                });
            }
            return (await instance.post(`${this.service}/checkLogin`)).data as CheckLoginResponseDto;
        } catch (e) {
            if (!retried && isAxiosError(e) && e.response?.status === 401 && await this.recoverExpiredViewAsRole()) {
                this.accessToken = '';
                return this.checkLogin(true);
            }
            throw this.transformException(e);
        }
    }

    /**
     * Deletes the login cookie at the authenticator and logs out locally. Does not reload, so the caller can run the
     * post-logout hook first and then call reloadToLogin().
     * sessionExpired: the session is invalid anyway (401), a failing request does not stop the local logout.
     * Otherwise a failing request throws and the person stays logged in (the cookie would still be valid).
     */
    static async logout(options: { sessionExpired?: boolean } = {}): Promise<void> {
        try {
            await Axios.create({withCredentials: true}).post(`${process.env.VUE_APP_API_PATH}/${this.service}/logout`);
        } catch (e) {
            if (!options.sessionExpired) {
                throw this.transformException(e);
            }
        }
        store.commit('logout');
        try {
            await NotifierSocketApi.disconnect();
        } catch (e) {
            // the reload closes the socket anyway
        }
    }

    /** Opens the login (or another public page, e.g. /logged-out) with a full reload, so nothing of the old session remains */
    static reloadToLogin(path = '/login') {
        window.location.replace(`${window.location.pathname}#${path}`);
        window.location.reload();
    }

    /**
     * Retrieves the user's crud access from permissioner and uses it to create
     * a new instance of CrudAccessManager in store.
     * This method was implemented here because using any child classes of Api.class.ts
     * before authorization results in an error.
     * Handle with care.
     */
    public static async setCrud(): Promise<void> {
        // Concurrent calls (e.g. fast navigation) share one request
        if (!this.crudRequest) {
            this.crudRequest = this.loadCrud().finally(() => {
                this.crudRequest = null;
            });
        }
        return this.crudRequest;
    }

    /**
     * Makes sure the user's access is available without blocking every navigation:
     * loads it once (awaited), afterwards it is only refreshed in the background when older than CRUD_MAX_AGE_MS.
     */
    public static async ensureCrud(): Promise<void> {
        if (!this.crudLoadedAt) {
            await this.setCrud();
            return;
        }
        if (Date.now() - this.crudLoadedAt > CRUD_MAX_AGE_MS) {
            this.setCrud().catch(() => {
                // Keep the known access, the next navigation tries again
            });
        }
    }

    private static async loadCrud(): Promise<void> {
        const axiosInstance = store.state.axios;
        if (!axiosInstance) {
            throw new Error('Axios instance not defined');
        }
        try {
            const [resp, guiResp]: [AxiosResponse<CrudAccess>, AxiosResponse<NavigationDrawerAccess>] = await Promise.all([
                axiosInstance.get(`${process.env.VUE_APP_API_PATH}/permissioner/crud`),
                axiosInstance.get(`${process.env.VUE_APP_API_PATH}/permissioner/gui`),
            ]);
            store.commit('setCrudAccess', resp.data);
            store.commit('setGuiAccess', guiResp.data);
            this.crudLoadedAt = Date.now();
        } catch (e) {
            throw this.transformException(e);
        }
    }

    /**
     * Handles the credentials received from the login endpoint and places them in the store
     * @param credentials - the credentials received from the login endpoint
     * @private
     */
    private static handleCredentials(credentials: any) {
        this.accessToken = credentials.token.access_token;
        // Different login, the access has to be loaded again
        this.crudLoadedAt = 0;
        store.commit('user', credentials.meta);
        store.commit('setLoginUserId', credentials.loginUserId);
        store.commit('login', this.accessToken);
        store.commit('setUserType', credentials.type);
    }

    private static transformException<T>(e: T): VanillaException | T {
        const vanillaException = ExceptionsCollector.tryCast(e);
        if (vanillaException) {
            return vanillaException;
        }
        return e;
    }

}
